import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, Form, Table, Alert, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
    excludeAudienceFromIvrCampaign,
    getExcludedAudienceFile,
    resetIvrState,
} from "../../store/slices/ivr";

function IvrExcludeAudience({
    showIvrExcludeAudienceModal,
    setShowIvrExcludeAudienceModal,
    campaign,
}) {
    const dispatch = useDispatch();
    const {
        fileData = [],
        loading = false,
        error: storeError = null,
        uploadSuccess = false,
    } = useSelector((state) => state.ivrReducer || {});

    const [selectedFile, setSelectedFile] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const fileInputRef = useRef(null); // for resetting input

    useEffect(() => {
        if (campaign?.data?.id) {
            dispatch(getExcludedAudienceFile({ campaign_id: campaign.data.id }));
        }
    }, [campaign?.data?.id, dispatch]);

    useEffect(() => {
        if (uploadSuccess) {
            dispatch(getExcludedAudienceFile({ campaign_id: campaign?.data?.id }));
            setContacts([]);
            setSelectedFile(null);
            setError("");
            setSuccess("Exclusion list submitted successfully.");
            if (fileInputRef.current) fileInputRef.current.value = ""; // clear file input
        }
    }, [uploadSuccess, dispatch, campaign?.data?.id]);

    const handleClose = () => {
        if (fileInputRef.current) fileInputRef.current.value = ""; // clear input on close
        setSelectedFile(null);
        setContacts([]);
        setError("");
        setSuccess("");
        dispatch(resetIvrState());
        setShowIvrExcludeAudienceModal(false);
    };

    const handleDownloadSample = () => {
        const sampleData = "SNo,Mobile Number\n1,*********\n";
        const blob = new Blob([sampleData], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "sample_exclude_contacts.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setSelectedFile(file);
        setContacts([]);
        setError("");
        setSuccess("");
    };

    const handleUpload = (e) => {
        e.preventDefault();

        if (!selectedFile) {
            setError("Please select a CSV file before uploading.");
            return;
        }

        if (
            selectedFile.type !== "text/csv" &&
            !selectedFile.name.toLowerCase().endsWith(".csv")
        ) {
            setError("Invalid file type. Please upload a valid CSV file.");
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const text = event.target.result;
                const lines = text.split("\n").filter((line) => line.trim() !== "");
                if (lines.length <= 1) {
                    setError("The uploaded file appears empty or invalid.");
                    return;
                }

                const parsedContacts = lines
                    .slice(1)
                    .map((line, index) => {
                        const [sno, mobile] = line.split(",");
                        const cleanMobile = mobile?.trim();
                        return { sno: sno?.trim() || index + 1, mobile: cleanMobile };
                    })
                    .filter(Boolean);

                if (parsedContacts.length === 0) {
                    setError("No valid contacts found in the CSV file.");
                    return;
                }

                setContacts(parsedContacts);
                setError("");
                setSuccess("Contacts uploaded successfully. Review before submitting.");
            } catch (err) {
                setError("Failed to process CSV file. Please check the format.");
            }
        };
        reader.onerror = () => setError("Error reading the selected file.");
        reader.readAsText(selectedFile);
    };

    const handleRemoveContact = (index) => {
        setContacts((prev) => prev.filter((_, i) => i !== index));
    };

    const generateCsvFile = (contacts) => {
        const header = "SNo,Mobile Number\n";
        const rows = contacts.map((c) => `${c.sno},${c.mobile}`).join("\n");
        return new Blob([header + rows], { type: "text/csv" });
    };

    const handleFinalSubmit = async () => {
        setError("");
        setSuccess("");

        if (!contacts.length) {
            setError("No contacts found to submit.");
            return;
        }

        try {
            const csvBlob = generateCsvFile(contacts);
            const user = JSON.parse(localStorage.getItem("user"));
            const userId = user?.data?.id;
            if (!userId) throw new Error("Invalid or missing user.");

            if (!campaign?.data?.id || !campaign?.data?.uuid) {
                throw new Error("Campaign details are incomplete.");
            }

            const formData = new FormData();
            formData.append("file", csvBlob, "final_exclude_contacts.csv");
            formData.append("userId", userId);
            formData.append("campaignId", campaign.data.id);
            formData.append("campaignUuid", campaign.data.uuid);

            await dispatch(excludeAudienceFromIvrCampaign(formData));

            // reset after successful dispatch
            if (fileInputRef.current) fileInputRef.current.value = "";
            setSelectedFile(null);
            setContacts([]);
            setSuccess("Exclusion list submitted successfully.");
        } catch (err) {
            setError(err.message || "Something went wrong while submitting.");
        }
    };

    const safeFileData = Array.isArray(fileData) ? fileData : [];

    return (
        <Modal
            show={showIvrExcludeAudienceModal}
            onHide={handleClose}
            centered
            size="lg"
        >
            <Modal.Header closeButton>
                <Modal.Title>Exclude Audience</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <p className="text-muted mb-3">
                    Upload a CSV file containing mobile numbers to exclude from this
                    campaign. You can preview and modify before final submission.
                </p>

                <div className="mb-3 d-flex justify-content-between align-items-center">
                    <Button variant="outline-primary" size="sm" onClick={handleDownloadSample}>
                        Download Sample CSV
                    </Button>
                </div>

                <Form onSubmit={handleUpload}>
                    <Form.Group controlId="formFile" className="mb-3">
                        <Form.Label>Upload CSV File</Form.Label>
                        <Form.Control
                            type="file"
                            accept=".csv"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            disabled={loading}
                        />
                    </Form.Group>

                    {error && <Alert variant="danger">{error}</Alert>}
                    {success && <Alert variant="success">{success}</Alert>}
                    {storeError && <Alert variant="danger">{storeError}</Alert>}

                    <div className="d-flex justify-content-end">
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? <Spinner animation="border" size="sm" /> : "Upload & Preview"}
                        </Button>
                    </div>
                </Form>

                {contacts.length > 0 && (
                    <div className="mt-4">
                        <h6>Preview Contacts to be Excluded</h6>
                        <div
                            style={{
                                maxHeight: "300px",
                                overflowY: "auto",
                                border: "1px solid #dee2e6",
                                borderRadius: "6px",
                            }}
                        >
                            <Table striped bordered hover size="sm" className="m-0">
                                <thead
                                    style={{
                                        position: "sticky",
                                        top: 0,
                                        background: "#f8f9fa",
                                        zIndex: 1,
                                    }}
                                >
                                    <tr>
                                        <th>S.No</th>
                                        <th>Mobile Number</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {contacts.map((item, index) => (
                                        <tr key={index}>
                                            <td>{item.sno}</td>
                                            <td>{item.mobile}</td>
                                            <td>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => handleRemoveContact(index)}
                                                >
                                                    Remove
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>

                        <div className="text-end mt-3">
                            <Button
                                variant="success"
                                onClick={handleFinalSubmit}
                                disabled={loading}
                            >
                                {loading ? (
                                    <Spinner animation="border" size="sm" />
                                ) : (
                                    "Submit Exclusion List"
                                )}
                            </Button>
                        </div>
                    </div>
                )}

                {safeFileData.length > 0 && (
                    <div className="mt-5">
                        <h6>Existing Excluded Contacts from S3</h6>
                        <div
                            style={{
                                maxHeight: "250px",
                                overflowY: "auto",
                                border: "1px solid #dee2e6",
                                borderRadius: "6px",
                            }}
                        >
                            <Table striped bordered hover size="sm" className="m-0">
                                <thead
                                    style={{
                                        position: "sticky",
                                        top: 0,
                                        background: "#f8f9fa",
                                        zIndex: 1,
                                    }}
                                >
                                    <tr>
                                        <th>S.No</th>
                                        <th>Mobile Number</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {safeFileData.map((row, index) => (
                                        <tr key={index}>
                                            <td>{index + 1}</td>
                                            <td>{row["Mobile Number"] || row.mobile}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default IvrExcludeAudience;
